import { z } from "zod"

export const UserSchema = z.object({
  extension: z.any(),
  id: z.number().int().gte(0),
  lastName: z.string(),
  disableRecordingWarning: z.boolean(),
  disableCheckOut: z.boolean(),
  name: z.string(),
  canEavesdrop: z.boolean(),
})

export const NextActionSchema = z.object({
  id: z.number().int().gte(0),
  questionnaireId: z.number().int().gte(0).nullish(),
  label: z.string(),
})

export const DepartmentSchema = z.object({
  id: z.number().int().gte(0),
  users: z.number().int().gte(0).array(),
  name: z.string(),
  hasQueueing: z.boolean(),
  deleted: z.boolean(),
})
export type TDepartment = z.infer<typeof DepartmentSchema>

export const DispositionSchema = z.object({
  id: z.number().int().gte(0),
  label: z.string(),
})
export type TDisposition = z.infer<typeof DispositionSchema>

export const RingoNumberSchema = z.object({
  id: z.number().int().gte(0),
  name: z.string(),
  number: z.string(),
})

export const CallerIDSchema = z
  .object({
    number: z.string().nullish(),
    label: z.string().nullish(),
    type: z.string().nullish(),
    selected: z.boolean().nullish().optional(),
  })
  .nullish()
export type TCallerID = z.infer<typeof CallerIDSchema>

export const VoicemailDropSchema = z.object({
  id: z.number().int().gte(0),
  alias: z.string(),
  url: z.string().url(),
})

export const EndpointSchema = z.object({
  id: z.number().int().gte(0).nullish(),
  callBlast: z.boolean().nullish(),
  personId: z.number().int().gte(0).nullish(),
  label: z.string().nullish(),
  number: z.string().nullish(),
  type: z.string().nullish(),
  deviceId: z.any().nullish(),
})

export const UserStatusSchema = z.object({
  accountId: z.coerce.number().int(),
  vmdropSetup: z.boolean(),
  disableCheckOut: z.boolean(),
  checkedIn: z.boolean(),
  phoneStatus: z.string(),
  id: z.coerce.number().int(),
  name: z.coerce.string(),
  lastName: z.coerce.string(),
  powerDialer: z.boolean(),
  companyName: z.coerce.string(),
  email: z.coerce.string(),
  disableRecordingWarnings: z.boolean().nullish(),
  departmentIds: z.number().int().gte(0).array(),
  isOwner: z.coerce.boolean(),
  isAdmin: z.coerce.boolean(),
  fullName: z.coerce.string(),
  canEavesdrop: z.boolean(),
  accountSmsEnabled: z.coerce.boolean(),
})
export type TUserStatus = z.infer<typeof UserStatusSchema>

export const AccountSchema = z.object({
  users: UserSchema.array(),
  accountSid: z.string(),
  sendSmsEnabled: z.boolean(),
  nextActions: NextActionSchema.array(),
  inTrial: z.boolean(),
  enableClickToCallOnlyInsideCrm: z.boolean(),
  departments: DepartmentSchema.array(),
  syncServiceSid: z.string(),
  timeZone: z.string(),
  expiredActiveDaysLeft: z.number().int(),
  dispositions: DispositionSchema.array(),
  powerDialerEnabled: z.boolean(),
  trialExpired: z.boolean(),
  // Email templates?
  machineDetectionEnabled: z.boolean(),
  crm: z.object({
    id: z.string(),
    availableText: z.string(),
    alias: z.string(),
    name: z.string(),
    saveNoteText: z.string(),
  }),
  enableEnterPhoneNumber: z.boolean(),
  testNumber: z.string().nullish(),
  timeToRingEndpointsSeconds: z.number().int().gte(0),
  status: z.string(),
  disableReferralLink: z.boolean(),
  blackListed: z.boolean(),
  adminEmail: z.string().email(),
  videoCallEnabled: z.boolean(),
  templatesEnabled: z.boolean(),
  // SMS templates?
  customCallerIdEnabled: z.boolean(),
  clusterId: z.string(),
  ringoNumbers: RingoNumberSchema.array(),
  supportPhoneNumber: z.string(),
})
export type TAccount = z.infer<typeof AccountSchema>

export const ClusterSchema = z.object({
  adminUrlS: z.coerce.string().url(),
  apiUrlS: z.coerce.string().url(),
  chromeExtensionWebstoreId: z.coerce.string(),
  launcherUrlS: z.coerce.string().url(),
  remoteUrlS: z.coerce.string().url(),
  voiceUrlS: z.coerce.string().url(),
})
export type TCluster = z.infer<typeof ClusterSchema>

export const UserPreferencesSchema = z.object({
  endPointStrategy: z.string(),
  callerIds: CallerIDSchema.array(),
  autoSms: z.boolean(),
  voicemailDrops: VoicemailDropSchema.array().nullish(),
  pvmEnabled: z.boolean(),
  nylasAccount: z
    .object({
      syncState: z.string(),
      lastSuccessfulConnection: z.coerce.date(),
      emailAddress: z.string().email(),
      provider: z.string(),
    })
    .nullish(),
  endPoints: EndpointSchema.array().nullish(),
  autoEmail: z.boolean(),
  webEndPointEnabled: z.boolean(),
  gettingStarted: z.boolean(),
  autoVoicemailDrop: z.boolean(),
  deviceEndPointEnabled: z.boolean(),
  autoProgress: z.boolean(),
})
export type TPreferences = z.infer<typeof UserPreferencesSchema>

export const LoginUserDataSchema = z.object({
  apiToken: z.coerce.string(),
  authToken: z.coerce.string(),
  cluster: ClusterSchema,
  account: AccountSchema,
  user: UserStatusSchema,
  preferences: UserPreferencesSchema,
  baseUrl: z.coerce.string().url(),
  twilio: z.object({
    twimlToken: z.coerce.string(),
    syncToken: z.coerce.string(),
    secondsTtl: z.coerce.number().int(),
    ids: z.object({
      userStatus: z.coerce.string(),
      userPreferences: z.coerce.string(),
      smartView: z.coerce.string(),
      lastRing: z.coerce.string(),
      smsHistories: z.coerce.string(),
      contacts: z.coerce.string(),
      intention: z.coerce.string(),
      roster: z.coerce.string(),
      departmentStats: z.coerce.string(),
      userStats: z.coerce.string(),
      account: z.coerce.string(),
      dialerList: z.coerce.string(),
      cluster: z.coerce.string(),
      liveCalls: z.coerce.string(),
      paymentStatus: z.coerce.string(),
      recentCalls: z.coerce.string(),
    }),
  }),
  dynamoDb: z.object({
    authorizationToken: z.coerce.string(),
    tokenTtlInSeconds: z.coerce.number().int(),
    accountId: z.any(),
    url: z.coerce.string().url(),
  }),
  xAuth: z.string().optional(),
})
export type TLoginUserData = z.infer<typeof LoginUserDataSchema>
